/**
 * The viewer's menu and what the landing needs to know, from the platform's reads (the org's packs, the brief, the
 * digest and study lists, the findings' view, the consultant's availability). The layout and the /app landing share
 * them, so the queries are read once. The rules themselves are pure (menu.ts, landing.ts).
 */
import { isLiveApi, useEnabledPacksQuery } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import { isPackEnabled, useTenantTheme } from "@/lib/tenant-theme";
import { knowledgeGateOf, knowledgeNavBadge, useKnowledgeLive, useKnowledgeView } from "@/lib/api/twin-knowledge";
import { digestNavVisible, useDigestsNavQuery } from "@/lib/api/digests";
import { studyNavVisible, useStudiesNavQuery } from "@/lib/api/studies";
import { useCopilotAvailability } from "@/lib/api/copilot-imports";
import { useImprovementProjectsEnabled, useImprovementProjectsSettingsQuery } from "@/lib/api/improvement-projects";
import { useBriefListQuery, useBriefLive, usePlantBriefQuery } from "@/lib/api/brief";
import { briefGapOf, briefNavVisible } from "@/lib/brief/state";
import { briefsMarkVisible } from "@/lib/brief/cadence-state";
import { useBriefsSeen } from "@/lib/brief/seen";
import { menuOf, type MenuArea, type MenuContext } from "./menu";
import type { BriefState, LandingContext } from "./landing";
import type { GateReads } from "./hubs";

export interface AppMenu {
  areas: MenuArea[];
  /** What decided the menu: a hub's strip of tabs reads it too (hubs.ts). */
  context: MenuContext;
  /** What the /app landing decides from, but for its own wait (landingOf's timedOut). */
  landing: Omit<LandingContext, "timedOut">;
  /** A read that decides an item is still on its way: an org switch's kept page waits for it. */
  pending: boolean;
  /** Each per-viewer read still on its way, by the context flag it decides: a hub's strip waits for its tabs' (hubPending). */
  reads: GateReads;
}

/** The brief's state for the landing: served, none written yet (or not served), failed, or still being read. */
export function briefStateOf(s: { live: boolean; hasData: boolean; error: unknown; loading: boolean }): BriefState {
  if (!s.live || s.hasData) return "served";
  if (s.error) return briefGapOf(s.error) === "failed" ? "failed" : "none";
  return s.loading ? "pending" : "none";
}

export function useAppMenu(): AppMenu {
  const tenant = useTenantTheme();
  const { activeOrg, isPlatformAdmin, isVendorAdmin, can } = useOrg();
  const vendorOrg = !!activeOrg?.isVendor;

  // The foundry pack: in live mode the active org's packs (server truth), else the local theme's toggle.
  const packs = useEnabledPacksQuery();
  const foundry = isLiveApi
    ? (packs.data?.packs ?? []).some((p) => p.pack === "foundry")
    : isPackEnabled(tenant, "foundry");
  const packsKnown = !isLiveApi || packs.isSuccess || packs.isError;
  const twin = foundry && !vendorOrg;

  // Knowledge: open where the findings' view is customer or internal (no pill), hidden otherwise.
  const knowledgeLive = useKnowledgeLive();
  const knowledgeView = useKnowledgeView(twin);
  const knowledgeOpen =
    knowledgeNavBadge(knowledgeGateOf(knowledgeLive, knowledgeView.data, knowledgeView.error)) === undefined;
  const digestsNav = useDigestsNavQuery(twin);
  const studiesNav = useStudiesNavQuery(twin);
  // The Consultant shows unless no consultant runs for the org ("off"): while its read is on its way, and where a role
  // may not use it or the read failed, its page says so (#1211 review, LOW 3: no pop-in, and no wait on its read).
  const consultantOpen = useCopilotAvailability() !== "off";
  // Improvement projects: a tab of Actions where the platform's gate and the org's switch are both on.
  const projects = useImprovementProjectsEnabled(twin);
  const projectsRead = useImprovementProjectsSettingsQuery(twin);

  // Today: shown where a brief is served or none is written yet (its page says so); its mark is a brief written since
  // the viewer's last visit (lib/brief/seen).
  const briefLive = useBriefLive();
  const brief = usePlantBriefQuery(twin);
  const dayBriefs = useBriefListQuery("day", twin);
  const shiftBriefs = useBriefListQuery("shift", twin);
  const seen = useBriefsSeen();
  const briefMark = briefsMarkVisible({
    newest: [dayBriefs.data?.items[0]?.generated_at, shiftBriefs.data?.items[0]?.generated_at],
    seen,
  })
    ? "New brief"
    : undefined;

  const context: MenuContext = {
    vendorOrg,
    vendorAdmin: isVendorAdmin,
    platformAdmin: isPlatformAdmin,
    foundry,
    can,
    brief: briefNavVisible({ live: briefLive, hasData: brief.data !== undefined, error: brief.error }),
    digests: digestNavVisible(digestsNav.data),
    studies: studyNavVisible(isPlatformAdmin, studiesNav.data),
    knowledgeOpen,
    consultantOpen,
    projects,
    briefMark,
  };
  const areas = menuOf(context);

  return {
    areas,
    context,
    landing: {
      vendorOrg,
      vendorAdmin: isVendorAdmin,
      packsKnown,
      foundry,
      brief: briefStateOf({
        live: briefLive,
        hasData: brief.data !== undefined,
        error: brief.error,
        loading: brief.isLoading,
      }),
    },
    pending:
      packs.isLoading ||
      brief.isLoading ||
      digestsNav.isLoading ||
      studiesNav.isLoading ||
      knowledgeView.isLoading ||
      projectsRead.isLoading,
    reads: {
      digests: digestsNav.isLoading,
      studies: studiesNav.isLoading,
      knowledgeOpen: knowledgeView.isLoading,
      projects: projectsRead.isLoading,
    },
  };
}
